
import { PasswordProtection } from "../components/PasswordProtection";
import { PageHeader } from "../components/PageHeader";
import SEO from "../components/SEO";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import GradientText from "../components/GradientText";
import FuzzyText from "../components/reactbits/FuzzyText";
import Silk from "../components/reactbits/Silk";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    Legend
} from "recharts";
import {
    Zap,
    TrendingUp,
    Building2,
    Users,
    CheckCircle2,
    BarChart3,
    DollarSign,
    HeartPulse
} from "lucide-react";
import { useState, useRef } from "react";
import { BentoGrid } from "../components/reactbits/BentoGrid";
import MagicBento from "../components/reactbits/MagicBento";
import { motion, AnimatePresence } from "framer-motion";
import { DemoRequestDialog } from "../components/DemoRequestDialog";

import { QuiltLogo } from "../components/QuiltLogo";
import PrahladImg from "../assets/prahlad.png";
import AshwinImg from "../assets/ashwin.png";
import WomanAppVideo from "../assets/woman-app.mp4";
import QuiltDemoVideo from "../assets/quilt-demo-video.mp4";
import QuiltPhoneImg from "../assets/quilt-phone.png";
import LadyCouchImg from "../assets/lady-couch.png";
import BoyRoomImg from "../assets/boy-room.png";
import LadyPorchImg from "../assets/lady-porch.png";

import Marquee from "../components/magicui/Marquee";

// Video imports
import bathVideo from "../assets/bath.mp4";
import hulkVideo from "../assets/hulk.mp4";
import lolaVideo from "../assets/lola.mp4";
import nutMonkeyVideo from "../assets/nut-monkey.mp4";
import retreat1Video from "../assets/retreat_full_1.mp4";
import retreat2Video from "../assets/retreat_full_2.mp4";
import suppVideo from "../assets/supp.mp4";
import PhoneMockup from "@/components/PhoneMockup";
import QuiltSwipeApp from "@/components/QuiltSwipeApp";
import { SolutionLoop } from "../components/SolutionLoop";
import { ThemeProvider } from "@/components/ThemeContext";
import DarkVeil from "../components/backgrounds/DarkVeil";
import ShinyText from "@/components/reactbits/ShinyText";
import { Timeline } from "../components/ui/timeline";
import RadialOrbitalEffect from "@/components/ui/radial-orbital-timeline";

// Section Components

const videos = [
    bathVideo,
    hulkVideo,
    lolaVideo,
    nutMonkeyVideo,
    retreat1Video,
    retreat2Video,
    suppVideo
];

const HeroSection = () => {

    return (
        <section className="relative pt-32 pb-8 md:pt-32 md:pb-4 overflow-hidden">

            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Badge variant="outline" className="mb-6 px-4 py-2 text-sm bg-indigo-500/10 border-indigo-500/30 text-indigo-500">
                            Quilt's Vision
                        </Badge>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-5xl md:text-7xl font-bold mb-8 tracking-tight text-slate-900 dark:text-white"
                    >
                        The Engine for <br />
                        <GradientText colors={["#a855f7", "#3b82f6", "#06b6d4"]} animationSpeed={6}>
                            Behavioral Change
                        </GradientText>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-xl md:text-2xl mb-10 max-w-2xl mx-auto text-slate-600 dark:text-slate-400"
                    >
                        A <strong className="text-slate-300">predictive engine</strong> that anticipates patient friction and <strong className="text-slate-300">generates</strong> hyper-personalized visual content in real-time to drive clinical adherence
                        {/* improving patient wellbing of the patient and patient engagement with their provider */}
                        {/* Bridging the engagement gap in the healthcare space with personalized wellness content for patients in real-time */}
                    </motion.p>
                </div>
            </div>

        </section>
    );
};

const WhatIsQuilt = () => {

    return (
        <section className="py-16 border-y bg-slate-50 border-slate-200 dark:bg-slate-900/50 dark:border-slate-800">
            <div className="container mx-auto px-4">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Visuals - Left Column */}
                    <div className="grid grid-cols-3 gap-4">
                        {[LadyCouchImg, BoyRoomImg, LadyPorchImg].map((img, i) => (
                            <div key={i} className="relative aspect-[2/3] rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-800">
                                <img
                                    src={img}
                                    alt={`Quilt User Context ${i + 1}`}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                        ))}
                    </div>

                    {/* Text - Right Column */}
                    <div className="text-left">
                        <p className="text-2xl md:text-3xl font-medium leading-relaxed text-slate-800 dark:text-slate-200">
                            Quilt is a <span className="text-indigo-500">predictive behavioral engine</span> that uses generative AI to create hyper-personalized content that drives patient adherence and retention
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ProblemSolution = () => {
    const videoRef = useRef<HTMLVideoElement>(null);

    const handleStepClick = (time: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
        }
    };

    return (
        <section className="pb-24 pt-12">
            <div className="mx-auto px-8">
                {/* Problem Section */}
                <div className="max-w-4xl mx-auto text-center mb-16">
                    <Badge variant="outline" className="mb-6 px-4 py-2 bg-red-500/10 border-red-500/30">
                        <span className="text-red-500 font-medium">The Problem</span>
                    </Badge>
                    <h2 className="text-3xl md:text-5xl mb-6 text-slate-900 dark:text-white">
                        <span className="text-red-500 font-medium">The</span>
                        <br />
                        <FuzzyText
                            fontSize="clamp(1.875rem, 5vw, 3rem)"
                            fontWeight={600}
                            baseIntensity={0.01}
                            hoverIntensity={0.05}
                            enableHover={true}
                            fuzzRange={30}
                            fps={24}
                        >
                            "Engagement Gap"
                        </FuzzyText>
                        <br />
                        <span className="text-red-500 font-medium">is Revenue Leakage</span>
                    </h2>
                    <p className="text-lg font-light text-slate-600 dark:text-slate-400">
                        Traditional engagement is passive, leading to high churn and reduced LTV.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-24">
                    {[
                        {
                            title: "High CAC, Low LTV",
                            description: "Acquisition costs are wasted when patients 'quietly quit' therapy early.",
                            icon: Users,
                        },
                        {
                            title: "Non-Adherence Costs",
                            description: "Lack of daily engagement drives complications and long-term care costs.",
                            icon: TrendingUp,
                        },
                        {
                            title: "Revenue Leakage",
                            description: "Billions lost annually to no-shows and missed billable interactions.",
                            icon: DollarSign,
                        },
                    ].map((item, index) => (
                        <Card
                            key={index}
                            className="p-6 border group transition-all duration-300 hover:scale-105 flex flex-col items-center text-center bg-white border-slate-200 hover:border-red-500/50 dark:bg-slate-900/50 dark:border-slate-800 dark:hover:border-red-500/50"
                        >
                            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                                <item.icon className="w-6 h-6 text-red-500" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3 text-slate-900 dark:text-white group-hover:text-red-500 transition-colors duration-300">
                                {item.title}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-400">
                                {item.description}
                            </p>
                        </Card>
                    ))}
                </div>



                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Left Side - Media */}
                    <div className="relative h-[720px] lg:h-auto lg:w-1/3 bg-slate-100 dark:bg-slate-950 rounded-2xl overflow-hidden">
                        <video
                            ref={videoRef}
                            className="absolute inset-0 w-full h-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                        >
                            <source src={QuiltDemoVideo} type="video/mp4" />
                        </video>
                    </div>

                    {/* Right Side - Content */}
                    <div className="relative p-8 lg:w-2/3 lg:p-12 flex flex-col justify-center gap-12 rounded-2xl overflow-hidden bg-white dark:bg-slate-900">

                        <div className="absolute inset-0 z-0 pointer-events-none opacity-40">
                            <DarkVeil />
                        </div>

                        {/* How Section */}
                        <div className="relative z-10">
                            {/* How Section - 4 Step Process */}
                            <div className="flex flex-col items-center">
                                <Badge variant="outline" className="mb-4 px-3 py-1 bg-emerald-500/10 border-emerald-500/30 text-emerald-500 text-center">Quilt's Solution</Badge>
                                {/* <h2 className="text-3xl md:text-4xl font-bold mb-8 text-slate-900 dark:text-white text-center">
                                    The Predictive Engine
                                </h2> */}

                                <div className="w-full">
                                    <SolutionLoop onStepClick={handleStepClick} />
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

const ResultsSection = () => {
    const effectData = [
        {
            id: 1,
            title: "Improvement in Adherence",
            date: "Metric: 40%",
            content: "Compared to standard-of-care static instructions.",
            category: "Clinical" as const,
            icon: TrendingUp,
            relatedIds: [2, 3],
            impact: 90
        },
        {
            id: 2,
            title: "Higher Retention",
            date: "Metric: 2.5x",
            content: "Patients stay in therapy longer, increasing LTV.",
            category: "Business" as const,
            icon: Users,
            relatedIds: [1],
            impact: 85
        },
        {
            id: 3,
            title: "Clinical Outreach",
            date: "Metric: Automated",
            content: "Clinics save 10+ hours/week on manual follow-ups.",
            category: "Operations" as const,
            icon: Zap,
            relatedIds: [1, 2],
            impact: 100
        },
        {
            id: 4,
            title: "Patient Satisfaction",
            date: "Metric: 95%",
            content: "High NPS scores and positive patient feedback.",
            category: "Experience" as const,
            icon: HeartPulse,
            relatedIds: [1, 2],
            impact: 80
        },
        {
            id: 5,
            title: "Cost Reduction",
            date: "Metric: 30%",
            content: "Lower overall cost of care delivery.",
            category: "Business" as const,
            icon: DollarSign,
            relatedIds: [2, 3],
            impact: 75
        }
    ];

    return (
        <section className="py-24 bg-emerald-50/50 dark:bg-emerald-900/10 border-y border-emerald-100 dark:border-emerald-900/20">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <Badge variant="outline" className="mb-6 px-4 py-2 bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
                        Proven Results
                    </Badge>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                        The Quilt Effect
                    </h2>
                    <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                        When patients feel understood, they stay engaged.
                    </p>
                </div>

                <div className="h-[600px] max-w-5xl mx-auto bg-black rounded-3xl overflow-hidden shadow-2xl relative border border-slate-800">
                    <RadialOrbitalEffect effectData={effectData} />
                </div>
            </div>
        </section>
    );
};

const ComparisonChart = () => {
    const rows = [
        {
            feature: "Core Focus",
            trad: "Administrative Record",
            consumer: "Content Library",
            quilt: "Behavioral Outcome"
        },

        {
            feature: "Patient Experience",
            trad: "Clunky Portal",
            consumer: "DIY / Self-Serve",
            quilt: "Guided & Proactive"
        },
        {
            feature: "Cost to Patient",
            trad: "Insurance / Co-pay",
            consumer: "Monthly Subscription",
            quilt: "Free (Provider Sponsored)"
        },

        {
            feature: "Personalization",
            trad: " ",
            consumer: "static 'Netflix-style' menu",
            quilt: "Generative & Predictive Content"
        },
        {
            feature: "Engagement Model",
            trad: " ",
            consumer: "Infinite Scroll",
            quilt: "high-impact Daily Stacks"
        },

        {
            feature: "Scalability",
            trad: " ",
            consumer: "Linear",
            quilt: "Exponential"
        },
        {
            feature: "Engagement Tracking",
            trad: " ",
            consumer: "Focuses on 'feeling good' in the moment",
            quilt: "targets symptoms through clinic admins"
        },
        {
            feature: "Audience Logic",
            trad: " ",
            consumer: "produced for the masses",
            quilt: "Content based on specific emotional triggers for every patient"
        },
        {
            feature: "Engagement Tracking",
            trad: " ",
            consumer: "suggest more of the 'same' content",
            quilt: "update the weights of patients Predictive Persona"
        },
    ];

    return (
        <section className="py-24 bg-slate-50 dark:bg-slate-900/30">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white">Different by Design</h2>
                    <p className="text-lg text-slate-600 dark:text-slate-400">The best of consumer engagement with the clinical rigor of healthcare.</p>
                </div>

                <div className="max-w-4xl mx-auto relative px-4 md:px-0">
                    {/* Floating Card Background - Continuous block for the middle column */}
                    <div className="absolute inset-y-0 left-1/3 right-1/3 -top-[1rem] -bottom-[2rem] bg-gradient-to-b from-transparent to-slate-900 shadow-2xl shadow-indigo-500/10 rounded-2xl border-b border-t border-indigo-100 dark:border-indigo-500/25 z-0 transform scale-y-105 scale-x-105" />

                    {/* Content Grid */}
                    <div className="relative z-10">
                        {/* Headers */}
                        <div className="grid grid-cols-3 font-semibold text-sm md:text-base mb-8">
                            <div className="text-slate-600 font-thin text-center tracking-wider pb-4">Traditional Platforms</div>

                            <div className="flex justify-center pb-4">
                                <div className="relative overflow-hidden flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1.5 rounded-full border-t border-indigo-100 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 font-bold">
                                    <QuiltLogo alt="Quilt Logo" className="size-8" />
                                    <ShinyText text="Quilt" className="tracking-[0.1rem] font-medium text-xl" speed={2} />
                                    <div className="shiny-bg absolute inset-0"></div>
                                </div>
                            </div>

                            <div className="text-slate-600 font-thin text-center tracking-wider pb-4">Consumer Wellness Apps</div>
                        </div>

                        {/* Rows */}
                        <div className="space-y-6">
                            {rows.map((row, i) => (
                                <div key={i} className="grid grid-cols-3 items-center group">
                                    {/* Traditional */}
                                    <div className="text-center text-sm md:text-base text-slate-500 px-2">
                                        {row.trad}
                                    </div>

                                    {/* Quilt */}
                                    <div className="text-center text-sm md:text-base text-indigo-700 dark:text-gray-200 px-2 relative">
                                        {row.quilt}
                                    </div>

                                    {/* Consumer */}
                                    <div className="text-center text-sm md:text-base text-slate-500 px-2">
                                        {row.consumer}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const MarketPositioning = () => {
    return (
        <section className="py-24 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white">Uncontested Market Space</h2>
                    <p className="text-lg text-slate-600 dark:text-slate-400">Positioned for both high engagement and clinical validity.</p>
                </div>

                <div className="relative max-w-4xl mx-auto aspect-square md:aspect-[16/9] bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 p-8">

                    {/* Axes */}
                    {/* Vertical Axis */}
                    <div className="absolute top-8 bottom-8 left-1/2 w-px bg-slate-300 dark:bg-slate-700">
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-sm font-semibold text-slate-500 whitespace-nowrap">High Engagement</div>
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-sm font-semibold text-slate-500 whitespace-nowrap">Low Engagement</div>
                    </div>
                    {/* Horizontal Axis */}
                    <div className="absolute left-8 right-8 top-1/2 h-px bg-slate-300 dark:bg-slate-700">
                        <div className="absolute left-0 -top-9 w-24 text-xs md:text-sm font-semibold text-slate-500 text-left md:-left-20 md:top-1/2 md:-translate-y-1/2 md:text-right md:w-16">Consumer / General</div>
                        <div className="absolute right-0 -top-9 w-24 text-xs md:text-sm font-semibold text-slate-500 text-right md:-right-20 md:top-1/2 md:-translate-y-1/2 md:text-left md:w-16">Clinical</div>
                    </div>

                    {/* Quilt Logo - Top Right Quadrant */}
                    <div className="absolute top-[20%] right-[20%] flex flex-col items-center">
                        <div className="relative cursor-pointer">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 backdrop-blur-3xl animate-pulse" />
                            <motion.div
                                className="relative w-24 z-10"
                                whileHover={{ scale: 1.1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 400,
                                    damping: 15
                                }}
                            >
                                <QuiltLogo alt="Quilt Logo" className="w-full h-auto" />
                            </motion.div>
                        </div>
                        <span className="mt-2 font-bold text-slate-900 dark:text-white">Quilt</span>
                    </div>

                    {/* Competitor placeholders (optional/implied) */}
                    {/* Bottom Left: Generic Content */}
                    <div className="absolute bottom-[20%] left-[20%] text-slate-400 text-sm">Traditional Portals</div>
                    {/* Top Left: Consumer Apps */}
                    <div className="absolute top-[20%] left-[20%] text-slate-400 text-sm">Consumer Wellness</div>
                    {/* Bottom Right: EMRs */}
                    <div className="absolute bottom-[20%] right-[20%] text-slate-400 text-sm">EHR / Point Solutions</div>

                </div>
            </div>
        </section>
    );
};

const MarketStats = () => {

    const features = [
        {
            Icon: TrendingUp,
            name: "$32.3B",
            description: "Market Size (2026) - Patient Engagement",
            href: "#",
            cta: "Learn more",
            background: <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-500/20 to-purple-500/10 opacity-50" />,
            className: "col-span-3 lg:col-span-1",
        },
        {
            Icon: BarChart3,
            name: "18.6%",
            description: "CAGR Through 2035",
            href: "#",
            cta: "View projections",
            background: <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500/20 to-cyan-500/10 opacity-50" />,
            className: "col-span-3 lg:col-span-1",
        },
        {
            Icon: DollarSign,
            name: "$150B",
            description: "Revenue Loss Due to No-Shows",
            href: "#",
            cta: "See breakdown",
            background: <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-500/20 to-orange-500/10 opacity-50" />,
            className: "col-span-3 lg:col-span-1",
        }
    ];

    return (
        <section className="py-24">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white">Market Opportunity</h2>
                </div>
                <BentoGrid className="auto-rows-[22rem]">
                    {features.map((feature, i) => (
                        <MagicBento
                            key={i}
                            className={feature.className}
                            textAutoHide={true}
                            enableStars={true}
                            enableSpotlight={true}
                            enableBorderGlow={true}
                            enableTilt={false}
                            enableMagnetism={false}
                            clickEffect={true}
                            spotlightRadius={400}
                            particleCount={12}
                            glowColor="132, 0, 255"
                            disableAnimations={false}
                        >
                            {feature.background}
                            <div className="relative z-20 h-full flex flex-col justify-between p-6">
                                <feature.Icon className="h-10 w-10 text-slate-700 dark:text-slate-300" />
                                <div>
                                    <h3 className="text-5xl font-bold mb-2 text-slate-900 dark:text-white">{feature.name}</h3>
                                    <p className={"text-slate-600 dark:text-slate-400 mb-4 text-lg"}>{feature.description}</p>
                                    {/* <Button variant="ghost" asChild size="sm" className="-ml-3">
                                        <a href={feature.href}>
                                            {feature.cta} <ArrowRightIcon className="ml-2 h-4 w-4" />
                                        </a>
                                    </Button> */}
                                </div>
                            </div>
                        </MagicBento>
                    ))}
                </BentoGrid>
            </div>
        </section>
    );
};

const Roadmap = () => {
    const timelineData = [
        {
            title: <span className="opacity-40">Dec 2025</span>,
            content: (
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                        Foundation
                    </h3>
                    <ul className="space-y-2">
                        {["Core AI Engine training completed", "Automated content pipelines established"].map((subItem, j) => (
                            <li key={j} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-500" />
                                <span>{subItem}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )
        },
        {
            title: <span className="opacity-40">Jan 2026</span>,
            content: (
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                        Infrastructure
                    </h3>
                    <ul className="space-y-2">
                        {["Platform Beta Launch", "Initial 3 Provider Contracts secured"].map((subItem, j) => (
                            <li key={j} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-500" />
                                <span>{subItem}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )
        },
        {
            title: <div className="flex flex-col gap-1 opacity-40">Feb 2026 <span className="text-base text-indigo-500 font-semibold">(Current)</span></div>,
            content: (
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                        Validation
                        <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span></span>
                    </h3>
                    <ul className="space-y-2">
                        {["3 Active Clinical Pilots initiated", "Early efficacy data collection"].map((subItem, j) => (
                            <li key={j} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-indigo-500" />
                                <span>{subItem}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )
        },
        {
            title: "Q2 2026",
            content: (
                <div className="mb-8 group">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                        Growth
                    </h3>
                    <div className="mb-3 text-base font-medium text-emerald-600 dark:text-emerald-400">
                        Goal: 50k Patients
                    </div>
                    <ul className="space-y-2">
                        {["Seed Round Closure", "Mobile App Launch", "HIPAA/SOC2 Certification"].map((subItem, j) => (
                            <li key={j} className="text-sm flex items-start gap-2 text-slate-600 dark:text-slate-400">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 opacity-70 block flex-shrink-0" />
                                <span>{subItem}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )
        },
        {
            title: "Q3 2026",
            content: (
                <div className="mb-8 group">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                        Family
                    </h3>
                    <div className="mb-3 text-base font-medium text-emerald-600 dark:text-emerald-400">
                        Parental Access
                    </div>
                    <ul className="space-y-2">
                        {["Parents Dashboard", "Family Account Management", "Guardian Alerts & Monitoring"].map((subItem, j) => (
                            <li key={j} className="text-sm flex items-start gap-2 text-slate-600 dark:text-slate-400">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 opacity-70 block flex-shrink-0" />
                                <span>{subItem}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )
        },
        {
            title: "Q4 2026",
            content: (
                <div className="mb-8 group">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                        Integration
                    </h3>
                    <div className="mb-3 text-base font-medium text-emerald-600 dark:text-emerald-400">
                        Enterprise Partnerships
                    </div>
                    <ul className="space-y-2">
                        {["EHR Write-back Integration", "Launch of 'Quilt Voice'", "Enterprise Pilot Expansion"].map((subItem, j) => (
                            <li key={j} className="text-sm flex items-start gap-2 text-slate-600 dark:text-slate-400">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 opacity-70 block flex-shrink-0" />
                                <span>{subItem}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )
        },
        {
            title: "2027+",
            content: (
                <div className="mb-8 group">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                        Platform
                    </h3>
                    <div className="mb-3 text-base font-medium text-emerald-600 dark:text-emerald-400">
                        Quilt SDK
                    </div>
                    <ul className="space-y-2">
                        {["Developer API / SDK Public Beta", "3rd Party Content Marketplace", "National Rollout"].map((subItem, j) => (
                            <li key={j} className="text-sm flex items-start gap-2 text-slate-600 dark:text-slate-400">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 opacity-70 block flex-shrink-0" />
                                <span>{subItem}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )
        }
    ];

    return (
        <section className="py-12 bg-slate-50 dark:bg-slate-900/30 w-full">
            <Timeline data={timelineData}>
                <div className="max-w-7xl mx-auto py-12 px-4 md:px-8 lg:px-10">
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white border-l-4 border-indigo-500 pl-4 mb-4">
                        Growth & Milestones
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 text-lg max-w-2xl">
                        Our journey from core infrastructure to a comprehensive behavior engine.
                    </p>
                </div>
            </Timeline>
        </section>
    );
};

// Main Page Component
function InvestorPortalContent() {
    const [analysisResult, setAnalysisResult] = useState<string | null>(null);


    return (
        <div className="min-h-screen bg-white dark:bg-black">
            <SEO
                title="Quilt Vision & Investor Portal | Generative Content Platform"
                description="Explore Quilt's market potential, financial trajectory, and innovative AI-driven personalized health streaming platform."
                path="/vision"
            />
            <PageHeader navItems={[
                { label: "Vision", id: "hero" },
                { label: "Solution", id: "solution" },
                { label: "Data", id: "data" },
                { label: "Roadmap", id: "roadmap" }
            ]} />

            {/* Background decoration */}

            <div className="absolute top-0 left-0 w-full h-svh max-h-[1280px] -z-1 pointer-events-none opacity-40">
                <Silk color={ThemeProvider.name === "dark" ? "#6f00d7ff" : "#581791ff"} />
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-white/40 to-transparent dark:from-black/100 dark:via-black/40 dark:to-transparent pointer-events-none" />
            </div>


            <div id="hero z-1">
                <HeroSection />
            </div>

            <div className="relative h-full min-h-[600px] lg:min-h-full flex flex-col lg:flex-row items-center justify-center gap-6 p-8 md:pb-24 z-1">
                <PhoneMockup className="transform scale-90 lg:scale-100 z-10">
                    <QuiltSwipeApp
                        onAnalysisComplete={(category) => setAnalysisResult(category)}
                        onReset={() => setAnalysisResult(null)}
                    />
                </PhoneMockup>

                {/* Analysis Bubbles - Desktop: Right side, Mobile: Stacked or adjusted */}
                <AnimatePresence>
                    {analysisResult && (
                        <>
                            {/* Top Bubble - Callout Style */}
                            <motion.div
                                initial={{ opacity: 0, x: -20, scale: 0.9 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: -10, scale: 0.95 }}
                                transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
                                className="relative z-0 w-full max-w-xs lg:max-w-none lg:absolute lg:top-[10%] lg:left-[calc(52%+160px)] lg:w-72"
                            >
                                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl shadow-xl relative">
                                    {/* Connector Line/Arrow */}
                                    <div className="hidden lg:block absolute top-8 -left-3 w-4 h-4 bg-white dark:bg-slate-900 border-l border-b border-slate-200 dark:border-slate-800 transform rotate-45"></div>

                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                            <QuiltLogo alt="Quilt Logo" className="w-12 h-12 mb-2" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-900 dark:text-white leading-tight">Quilt Analysis</h3>
                                            <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Engine Output</p>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-slate-600 dark:text-slate-400 text-sm">Preference Detected:</p>
                                        <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-500">
                                            {analysisResult}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Bottom Bubble - Context/Explanation - Left Side */}
                            <motion.div
                                initial={{ opacity: 0, x: 20, scale: 0.9 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: 10, scale: 0.95 }}
                                transition={{ duration: 0.5, delay: 0.6, type: "spring" }}
                                className="relative z-0 w-full max-w-xs lg:max-w-none lg:absolute lg:bottom-[20%] lg:right-[calc(50%+180px)] lg:w-80"
                            >
                                <div className="bg-slate-50 dark:bg-slate-800/50 backdrop-blur border border-slate-200 dark:border-slate-700 p-5 rounded-2xl shadow-lg relative">
                                    {/* Connector Line/Arrow - Pointing Right */}
                                    <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-4 bg-slate-50 dark:bg-slate-800/50 border-r border-t border-slate-200 dark:border-slate-700 transform rotate-45"></div>

                                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                        <span className="text-indigo-500 font-semibold block mb-1">Why this matters:</span>
                                        Based on your interactions, Quilt will now automatically generate and serve <span className="font-medium text-slate-900 dark:text-white">{analysisResult}</span> content to maximize your daily retention.
                                    </p>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>

            {/* <WhatIsQuilt /> */}

            <div id="solution">
                <ProblemSolution />

                {/* Video Marquee Section */}
                <div className="relative flex w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-transparent py-12 md:py-20">
                    <Marquee pauseOnHover className="[--duration:40s]">
                        {videos.map((video, index) => (
                            <div key={index} className="mx-2 h-64 md:h-80 w-auto overflow-hidden bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800">
                                <video
                                    src={video}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    controls={false}
                                    className="h-full w-auto object-cover"
                                />
                            </div>
                        ))}
                    </Marquee>
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-slate-50 dark:from-black"></div>
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-slate-50 dark:from-black"></div>
                </div>

                <ResultsSection />
            </div>

            <div id="data">
                <ComparisonChart />
                {/* <MarketPositioning /> */}
                <MarketStats />
            </div>

            <div id="roadmap">
                <Roadmap />
            </div>

            <section className="py-24 bg-slate-50 dark:bg-slate-900/30 border-t border-slate-200 dark:border-slate-800">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8 items-stretch">

                        {/* Prahlad Krishnan */}
                        <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col items-center text-center">
                            <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-indigo-100 dark:border-indigo-900/50">
                                <img src={PrahladImg} alt="Prahlad Krishnan" className="w-full h-full object-cover" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Prahlad Krishnan</h3>
                            <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4">Founder & CEO</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                Prahlad Krishnan is a serial entrepreneur in media tech, AI, and behavioral health. He built and sold one of the first EHR-integrated chatbot platforms to a leading sales AI company and a neurofeedback VR gaming startup to the world’s largest publicly-traded psychedelic pharmaceutical company.
                            </p>
                        </div>

                        {/* Ashwin Chandrasekaran */}
                        <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col items-center text-center">
                            <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-indigo-100 dark:border-indigo-900/50">
                                <img src={AshwinImg} alt="Ashwin Chandrasekaran" className="w-full h-full object-cover" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Ashwin Chandrasekaran</h3>
                            <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4">CTO</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                Ashwin Chandrasekaran is an engineer, founder, and serial technology lead in media tech and AI. He has built and lead development teams in areas ranging from advanced deep learning frameworks for neurofeedback and scalable IoT architectures to integrated enterprise solutions.
                            </p>
                        </div>

                        {/* Contact CTA */}
                        <div className="bg-indigo-600 dark:bg-indigo-900/20 p-8 rounded-2xl border border-indigo-500 dark:border-indigo-500/30 flex flex-col items-center justify-center text-center">
                            <h2 className="text-3xl font-bold mb-4 text-white">Partner with us</h2>
                            <p className="text-indigo-100 dark:text-indigo-300 mb-8 max-w-xs">
                                Join us in building the operating system for human engagement.
                            </p>
                            <DemoRequestDialog triggerText="Contact Founder" source="investor_contact">
                                <Button size="lg" className="bg-white text-indigo-600 hover:bg-slate-100 font-semibold text-lg px-8">
                                    Contact Founder
                                </Button>
                            </DemoRequestDialog>

                        </div>

                    </div>

                    {/* Team Pedigree / Logos */}
                    <div className="mt-20 pt-10 border-t border-slate-200 dark:border-slate-800">
                        <p className="text-center text-sm font-semibold text-slate-500 uppercase tracking-wider mb-8">Leadership Experience From</p>
                        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-300">
                            {/* Placeholder Logos - In a real scenario, these would be <img> tags */}
                            <div className="text-xl font-bold text-slate-400 flex items-center gap-2"><Building2 className="w-6 h-6" /> Duke University</div>
                            <div className="text-xl font-bold text-slate-400 flex items-center gap-2"><Building2 className="w-6 h-6" /> Tai Life Sciences</div>
                            <div className="text-xl font-bold text-slate-400 flex items-center gap-2"><Building2 className="w-6 h-6" /> Cybin</div>
                            <div className="text-xl font-bold text-slate-400 flex items-center gap-2"><Building2 className="w-6 h-6" /> Tremea</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default function InvestorPortal() {
    return (
        <PasswordProtection>
            <InvestorPortalContent />
        </PasswordProtection>
    );
}
